import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom"; // react-router-dom instead of react-router
import { UserAuth } from "../context/AuthContext";
import SocialAuthButton from "./SocialAuthButton";
import { Eye, EyeOff, Mail } from "lucide-react";
import bgleft from "../../assets/supbase/bgleft.svg";
import {
  Box,
  Flex,
  Heading,
  Text,
  Input,
  Button,
  FormLabel,
  FormControl,
  InputGroup,
  InputRightElement,
  Spinner,
  Link,
  Image,
} from "@chakra-ui/react";
import waveflowStudioLogo from "../../assets/supbase/waveflowStudioLogo.svg";

const Signup = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const navigate = useNavigate();

  const [resendSuccess, setResendSuccess] = useState(false);
  const { signUpNewUser, signInWithGoogle, signInWithGithub, verifySignUpCode, resendConfirmationEmail } = UserAuth();

  // DISABLED: OAuth temporarily removed during Cognito migration.
  // Email/password authentication only.
  /*
  const handleSocialSignIn = async (provider) => {
    setSocialLoading(provider);
    const { error } =
      provider === "google"
        ? await signInWithGoogle()
        : await signInWithGithub();
    setSocialLoading(null);
    if (error) setError(error.message);
  };
  */

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await verifySignUpCode(email, verificationCode);
      if (result.success) {
        setVerificationSuccess(true);
        setTimeout(() => {
          navigate("/signin");
        }, 3000);
      } else {
        setError(result.error?.message || result.error || "Verification failed. Please check the code and try again.");
      }
    } catch (err) {
      setError(err.message || "An error occurred during verification.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!email || !password || !name) {
      setError("Please fill in all fields");
      setLoading(false);
      return;
    }

    if (password?.length < 6) {
      setError("Password must be at least 6 characters");
      setLoading(false);
      return;
    }

    try {
      const result = await signUpNewUser(email, password, name);
      if (result.success) {
        setEmailSent(true);
      } else {
        setError(
          result.error?.message || "Failed to sign up. Please try again."
        );
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError(null);
    setResendSuccess(false);
    try {
      const result = await resendConfirmationEmail(email);
      if (result.success) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        setError(
          result.error?.message ||
          result.error ||
            "Failed to resend confirmation. Please try again."
        );
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <Flex minHeight="100vh" bg="white">
        {/* Left side with background and text */}
        <Box
          flex="3"
          bgImage={`url(${bgleft})`}
          bgSize="cover"
          bgPosition="center"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            position="absolute"
            top="1rem"
            left="1rem"
            fontSize="20px"
            fontWeight={500}
            color="white"
            fontFamily="ave"
          >
            <Flex align="center">
              <Image
                src={waveflowStudioLogo}
                height="36px"
                width="40px"
                mr="1"
              />
              <Text as="span" fontFamily="W">
                W
              </Text>
              aveflow Studio
            </Flex>
          </Box>

          <Box textAlign="center" color="white" px={6}>
            <Heading
              fontSize="3xl"
              fontWeight={700}
              mb={6}
              lineHeight="short"
              color="#DDDFFC"
            >
              Business Workflow Automation with
              <br />
              <Box as="span" fontSize="3xl" fontWeight={700} color="#FFF">
                #Waveflow–Studio
              </Box>
            </Heading>
            <Text
              fontSize="xl"
              bgGradient="linear(to-r, blue.600, purple.600)"
              bgClip="text"
              fontWeight={500}
              color={"#F5F5F5"}
            >
              Powerful Visual Coding using Natural Language
            </Text>
          </Box>
        </Box>

        {/* Right side */}
        <Flex flex="2" align="center" justify="center" px={8} minHeight="100vh">
          <Box maxW="md" textAlign="center" py={12} px={6} w="full">
            <Flex justify="center" mb={6}>
              <Box
                bg="purple.100"
                rounded="full"
                w={24}
                h={24}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Mail color="#6B46C1" size={40} />
              </Box>
            </Flex>

            <Heading fontSize="3xl" mb={4} color="gray.800">
              Please verify your email
            </Heading>

            <Box mb={4}>
              <Text fontSize="lg" color="gray.600">
                You're almost there! We sent an email to
              </Text>
              <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                {email}
              </Text>
            </Box>

            {error && (
              <Box
                mb={4}
                p={3}
                bg="red.50"
                color="red.600"
                rounded="md"
                fontSize="sm"
              >
                {error}
              </Box>
            )}

            {resendSuccess && (
              <Box
                mb={4}
                p={3}
                bg="green.50"
                color="green.700"
                rounded="md"
                fontSize="sm"
              >
                A new verification code has been sent to your email.
              </Box>
            )}

            {verificationSuccess ? (
              <Box
                mb={4}
                p={3}
                bg="green.50"
                color="green.600"
                rounded="md"
                fontSize="sm"
              >
                Email verified successfully! Redirecting to sign in...
              </Box>
            ) : (
              <form onSubmit={handleVerifyCode}>
                <FormControl mb={4} isRequired>
                  <FormLabel fontSize="sm" color="gray.700" textAlign="center">
                    ENTER 6-DIGIT VERIFICATION CODE
                  </FormLabel>
                  <Input
                    type="text"
                    placeholder="123456"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    textAlign="center"
                    fontSize="lg"
                    letterSpacing="4px"
                    maxLength={6}
                    focusBorderColor="blue.500"
                  />
                </FormControl>
                <Button
                  type="submit"
                  isLoading={loading}
                  colorScheme="purple"
                  w="full"
                  mb={4}
                >
                  Verify Code
                </Button>
              </form>
            )}

            <Box mb={4} color="gray.600">
              <Text>Still can't find the email? No problem.</Text>
            </Box>

            <Button
              onClick={handleResend}
              bgGradient="linear(to-r, blue.600, purple.600)"
              _hover={{ bgGradient: "linear(to-r, blue.700, purple.700)" }}
              color="white"
              px={6}
              py={3}
              rounded="md"
              mb={4}
              isLoading={loading}
              loadingText="Resending..."
            >
              Resend confirmation code
            </Button>

            <Text>
              Already have an account?{" "}
              <Link
                as={RouterLink}
                to="/signin"
                color="blue.600"
                _hover={{ textDecoration: "underline" }}
              >
                Sign in
              </Link>
            </Text>
          </Box>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex minHeight="100vh" bg="white">
      {/* Left side with background and text */}
      <Box
        flex="3"
        bgImage={`url(${bgleft})`}
        bgSize="cover"
        bgPosition="center"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          position="absolute"
          top="1rem"
          left="1rem"
          fontSize="20px"
          fontWeight={500}
          color="white"
          fontFamily="ave"
        >
          <Flex align="center">
            <Image src={waveflowStudioLogo} height="36px" width="40px" mr="1" />
            <Text as="span" fontFamily="W">
              W
            </Text>
            aveflow Studio
          </Flex>
        </Box>

        <Box textAlign="center" color="white" px={6}>
          <Heading
            fontSize="3xl"
            fontWeight={700}
            mb={6}
            lineHeight="short"
            color="#DDDFFC"
          >
            Business Workflow Automation with
            <br />
            <Box as="span" fontSize="3xl" fontWeight={700} color="#FFF">
              #Waveflow–Studio
            </Box>
          </Heading>
          <Text
            fontSize="xl"
            bgGradient="linear(to-r, blue.600, purple.600)"
            bgClip="text"
            fontWeight={500}
            color={"#F5F5F5"}
          >
            Powerful Visual Coding using Natural Language
          </Text>
        </Box>
      </Box>

      <Flex
        h="100vh"
        flex="2"
        align="center"
        justify="center"
        px={8}
        overflow="auto"
      >
        <Box
          maxW="md"
          w="full"
          p={8}
          borderWidth="1px"
          borderColor="gray.100"
          rounded="2xl"
          boxShadow="lg"
          bg="white"
        >
          <Flex justify="space-between" align="center" mb={6}>
            <Heading fontSize="2xl" fontWeight="semibold" color="#242424">
              Sign up & Automate your Business Workflows
            </Heading>
          </Flex>

          {error && (
            <Box
              mb={4}
              p={3}
              bg="red.50"
              color="red.600"
              rounded="md"
              fontSize="sm"
            >
              {error}
            </Box>
          )}

          <form onSubmit={handleSignUp}>
            {/* DISABLED: OAuth temporarily removed during Cognito migration. */}
            {/* Email/password authentication only. */}
            {/* <Flex justify="center" mb={6} gap={3}>
              <SocialAuthButton
                provider="google"
                onClick={() => handleSocialSignIn("google")}
                loading={socialLoading === "google"}
              />
              <SocialAuthButton
                provider="github"
                onClick={() => handleSocialSignIn("github")}
                loading={socialLoading === "github"}
              />
            </Flex> */}

            {/* <Box position="relative" mb={6}>
              <Box
                borderTop="1px"
                borderColor="gray.300"
                position="absolute"
                inset={0}
                top="50%"
                transform="translateY(-50%)"
              />
              <Text
                position="relative"
                bg="white"
                textAlign="center"
                fontSize="sm"
                color="gray.500"
                px={2}
                maxW="max-content"
                mx="auto"
              >
                OR
              </Text>
            </Box> */}

            <FormControl mb={4} isRequired>
              <FormLabel fontSize="xs" fontWeight={500} color="#757575">
                NAME
              </FormLabel>
              <Input
                type="text"
                placeholder="yourname"
                value={name}
                onChange={(e) => setName(e.target.value)}
                focusBorderColor="blue.500"
                borderColor="gray.300"
                borderRadius={0}
                variant={"flushed"}
                // py={2}
              />
            </FormControl>

            <FormControl mb={4} isRequired>
              <FormLabel fontSize="xs" fontWeight={500} color="#757575">
                EMAIL
              </FormLabel>
              <Input
                type="email"
                placeholder="yourname@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                focusBorderColor="blue.500"
                variant={"flushed"}
                borderColor="gray.300"
                borderRadius={0}
                // py={2}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="xs" fontWeight={500} color="#757575">
                PASSWORD
              </FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="***************"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  focusBorderColor="blue.500"
                  variant={"flushed"}
                  borderColor="gray.300"
                  borderRadius={0}
                  py={2}
                />
                <InputRightElement>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    variant="ghost"
                    color="gray.400"
                    _hover={{ color: "blue.600" }}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                </InputRightElement>
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Must be at least 6 characters
              </Text>
            </FormControl>

            <Button
              type="submit"
              colorScheme="purple"
              w="full"
              py={4}
              fontWeight="semibold"
              fontSize="md"
              mt={6}
              isLoading={loading}
              loadingText="Signing up..."
            >
              Sign Up
            </Button>
          </form>

          <Text textAlign="center" fontSize="xs" mt={2} color="#424242">
            Own an Account?{" "}
            <Link
              as={RouterLink}
              to="/signin"
              color="#212121"
              fontWeight="semibold"
              _hover={{ textDecoration: "underline" }}
            >
              JUMP RIGHT IN
            </Link>
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
};

export default Signup;
